
import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { Camera, Upload } from "lucide-react";

interface AvatarUploadSectionProps {
  profile: {
    avatar_url: string;
    full_name: string;
  };
  onAvatarUpdate: (avatarUrl: string) => void;
}

const AvatarUploadSection: React.FC<AvatarUploadSectionProps> = ({ profile, onAvatarUpdate }) => {
  const [uploadLoading, setUploadLoading] = useState(false);
  const { toast } = useToast();

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // التحقق من نوع الملف
    if (!file.type.startsWith('image/')) {
      toast({
        title: "خطأ في نوع الملف",
        description: "يرجى اختيار صورة صالحة",
        variant: "destructive",
      });
      return;
    }

    // التحقق من حجم الملف (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "حجم الملف كبير",
        description: "يرجى اختيار صورة أصغر من 5 ميجابايت",
        variant: "destructive",
      });
      return;
    }

    setUploadLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('المستخدم غير مسجل');
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Math.random()}.${fileExt}`;

      // رفع الملف إلى التخزين
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          upsert: true
        });

      if (uploadError) {
        throw uploadError;
      }

      // الحصول على رابط الصورة العام
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // تحديث الملف الشخصي برابط الصورة الجديد
      const { error: updateError } = await supabase
        .from('tenants')
        .update({ avatar_url: publicUrl })
        .eq('user_id', user.id);

      if (updateError) {
        throw updateError;
      }

      onAvatarUpdate(publicUrl);
      toast({
        title: "تم رفع الصورة",
        description: "تم تحديث صورتك الشخصية بنجاح",
      });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: "خطأ في رفع الصورة",
        description: "حدث خطأ أثناء رفع الصورة. حاول مرة أخرى.",
        variant: "destructive",
      });
    }
    setUploadLoading(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-right flex items-center gap-2">
          <Camera className="h-5 w-5 text-blue-600" />
          الصورة الشخصية
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center space-y-4">
          <Avatar className="h-32 w-32">
            <AvatarImage src={profile.avatar_url} alt={profile.full_name} />
            <AvatarFallback className="text-2xl">
              {profile.full_name ? profile.full_name.charAt(0) : 'م'}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex flex-col items-center space-y-2">
            <Label htmlFor="avatar-upload" className="cursor-pointer">
              <div className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                <Upload className="h-4 w-4" />
                {uploadLoading ? "جاري الرفع..." : "تغيير الصورة"}
              </div>
            </Label>
            <Input
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
              disabled={uploadLoading}
            />
            <p className="text-sm text-gray-500 text-center">
              اختر صورة شخصية (PNG, JPG, GIF) - حد أقصى 5 ميجابايت
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AvatarUploadSection;
